import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import AnnouncementBar from "@/components/AnnouncementBar";
import Footer from "@/components/Footer";

export const INSIGHTS = [
  {
    slug: "ai-native-lead-gen-framework",
    tag: "Framework",
    date: "Feb 2026",
    read: "9 min",
    title: "The AI-Native Lead Gen Framework, explained.",
    excerpt:
      "Six stages, one operating model — how RevGrow orchestrates signal detection, intent scoring, activation and continuous attribution under a single accountable partner.",
    hero: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "From advisory to end-to-end delivery",
        p: "European B2B buyers no longer want strategy and execution stitched together across four vendors. Our Demand Generation Delivery vertical exists to remove that friction — one team, from insight to booked meeting.",
      },
      {
        h: "The six stages",
        p: "Signal detection → intent scoring → multi-channel activation → real-time qualification → live CRM handover → continuous attribution. Every stage is instrumented, so pipeline is a priority list, not a guess.",
      },
      {
        h: "Why AI belongs in every stage — not just at the top",
        p: "AI at the top of funnel makes noise. AI at every stage makes pipeline. Continuous re-scoring means cooling accounts drop and warming ones rise — automatically, weekly.",
      },
    ],
  },
  {
    slug: "cio-playbook-2026-genai-uk-enterprise",
    tag: "Whitepaper",
    date: "Feb 2026",
    read: "14 min",
    title: "The CIO Playbook 2026: GenAI in the UK Enterprise.",
    excerpt:
      "A benchmark of GenAI deployment maturity across 250 UK enterprises — where CIOs are winning budget, where they are stuck, and what actually moved in the last twelve months.",
    hero: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "The UK enterprise picture",
        p: "62% of CIOs surveyed said their GenAI budget grew year-on-year, but only 18% report a P&L-visible outcome so far. The gap is closed by three factors — governance, data plumbing and vendor choice.",
      },
      {
        h: "Governance still runs ahead of scale",
        p: "The most mature deployments have a written AI acceptable-use policy and an internal review board. Without those, pilots stall.",
      },
      {
        h: "Where budgets moved in 2025",
        p: "Away from horizontal copilots, toward vertical, ROI-provable use cases — service ops, contact centre routing, compliance summarisation and dev productivity.",
      },
    ],
  },
  {
    slug: "signal-not-spray-b2b-2026",
    tag: "Point of view",
    date: "Feb 2026",
    read: "6 min",
    title: "Signal, not spray-and-pray: what changes in B2B 2026.",
    excerpt:
      "Why the winning demand-gen playbook for 2026 favours narrow intent signals across the whole buying committee, and how to move from list-first to signal-first thinking.",
    hero: "https://images.unsplash.com/photo-1554774853-aae0a22c8aa4?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "Death of the static ICP list",
        p: "A static list of 5,000 accounts is a snapshot, not a pipeline. Signal-first prioritisation looks at 15+ live signal types and re-ranks weekly.",
      },
      {
        h: "The buying committee, resolved",
        p: "One CIO doesn't buy anymore — a committee does. Resolving the committee at account level (CIO + VP Eng + Head of Security) is the difference between one meeting and a closable opportunity.",
      },
      {
        h: "Attribution finally becomes useful",
        p: "Closed-loop attribution ties spend to pipeline by product, region and campaign. If your board is still asking 'which channel worked?', you don't have attribution — you have a spreadsheet.",
      },
    ],
  },
  {
    slug: "post-cloud-cost-uk",
    tag: "Report",
    date: "Jan 2026",
    read: "11 min",
    title: "The post-cloud cost optimisation guide for UK CFOs.",
    excerpt:
      "Where UK CFOs are finding the next 15% of infrastructure savings — FinOps maturity models, workload placement, and the emerging repatriation playbook.",
    hero: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "The 40-30-30 rule",
        p: "Mature FinOps organisations report their savings roughly split 40% commitments, 30% workload right-sizing, 30% architectural changes. The last 30% is where 2026's savings live.",
      },
      {
        h: "Selective repatriation is real",
        p: "For predictable, high-throughput workloads, private infra is now up to 55% cheaper over 3 years. But only if the platform team can operate it — that is the real cost.",
      },
      {
        h: "What CFOs should ask in 2026",
        p: "Not 'what's our cloud bill?' but 'what's our unit economics per feature shipped?'. The answer forces engineering and finance into the same conversation.",
      },
    ],
  },
  {
    slug: "abm-signal-scoring-matrix",
    tag: "Playbook",
    date: "Jan 2026",
    read: "7 min",
    title: "Multi-source intent scoring: the ABM matrix that actually works.",
    excerpt:
      "From five intent sources to one consolidated score — the exact weighting we use to prioritise 281 target accounts across ANZ, EMEA and North America.",
    hero: "https://images.unsplash.com/photo-1590650153855-d9e808231d41?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "Five sources, one score",
        p: "Predictor, Bombora, ZoomInfo, LinkedIn engagement, first-party. Weighted, normalised, combined into a single 0–100 score.",
      },
      {
        h: "Weighting is not equal",
        p: "First-party signals get 1.4× weight because they show real engagement with your brand. Third-party is directional; first-party is confirmatory.",
      },
      {
        h: "Dark-account lift",
        p: "A first-party overlay surfaced 14 previously-unresearched accounts in a recent quarter — accounts no third-party list had marked as in-market.",
      },
    ],
  },
  {
    slug: "revgrow-launches-hyderabad-delivery-centre",
    tag: "News",
    date: "May 2026",
    read: "3 min",
    title: "RevGrow opens Hyderabad delivery centre.",
    excerpt:
      "A dedicated Demand Generation Delivery practice, supported by a high-calibre offshore team, extends RevGrow from advisory into end-to-end demand execution.",
    hero: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=70",
    body: [
      {
        h: "One accountable partner",
        p: "Clients have been clear — they want strategic clarity and execution muscle from one partner. The new practice is our direct response, covering GTM design, campaign build, in-market execution and qualified pipeline.",
      },
      {
        h: "Talent, not outsourcing",
        p: "Hyderabad gives us access to world-class talent in performance marketing, paid media operations and B2B data engineering — embedded inside RevGrow rather than partnered externally.",
      },
      {
        h: "What clients gain",
        p: "Integrated delivery, full-funnel visibility, faster activation — without compromising quality or scale. A rare combination in the European B2B market.",
      },
    ],
  },
];

export default function Insights() {
  const [featured, ...rest] = INSIGHTS;
  return (
    <main data-testid="insights-page" className="relative min-h-screen">
      <AnnouncementBar />
      <Navbar />

      <section className="relative pt-32 md:pt-40 pb-8 md:pb-14">
        <div className="absolute inset-0 grid-bg opacity-30 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <Link
            to="/"
            data-testid="insights-back-link"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal mb-8"
          >
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Insights Hub · Point-of-view from the RevGrow team
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <h1 className="lg:col-span-8 font-display font-semibold text-4xl md:text-5xl lg:text-[80px] leading-[0.96] tracking-[-0.03em]">
              Signals, not <span className="italic font-normal text-teal">soundbites.</span>
            </h1>
            <p className="lg:col-span-4 text-base text-muted-foreground max-w-md leading-relaxed">
              Frameworks, playbooks and hard numbers from real European B2B
              campaigns. Also published in long-form on our owned flagship,
              TechBytes.org.uk.
            </p>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="pb-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Link
            to={`/insights/${featured.slug}`}
            data-testid={`insight-featured-${featured.slug}`}
            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-2xl border border-white/[0.06] bg-[#0A121A] overflow-hidden card-hover"
          >
            <div className="lg:col-span-6 relative overflow-hidden aspect-[16/10] lg:aspect-auto">
              <img
                src={featured.hero}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A121A] via-transparent to-transparent" />
            </div>
            <div className="lg:col-span-6 p-7 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-5">
                <span className="rounded-full border border-teal/30 bg-teal/[0.06] text-teal text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1">
                  Featured · {featured.tag}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground inline-flex items-center gap-1">
                  <Clock size={11} /> {featured.read}
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold leading-tight tracking-tight mb-4">
                {featured.title}
              </h2>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg">
                {featured.excerpt}
              </p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-teal text-sm">
                Read the piece <ArrowUpRight size={15} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="flex items-baseline justify-between mb-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal">
              Latest
            </div>
            <a
              href="https://techbytes.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal inline-flex items-center gap-1"
            >
              More on TechBytes.org.uk <ArrowUpRight size={12} />
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map((i) => (
              <Link
                key={i.slug}
                to={`/insights/${i.slug}`}
                data-testid={`insight-card-${i.slug}`}
                className="group rounded-2xl border border-white/[0.06] bg-[#0A121A] overflow-hidden card-hover flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={i.hero}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:scale-105 group-hover:opacity-90 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A121A]/80 to-transparent" />
                  <div className="absolute top-3 left-3 rounded-full border border-teal/30 bg-teal/10 text-teal text-[10px] font-mono uppercase tracking-[0.18em] px-2.5 py-1">
                    {i.tag}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-3">
                    {i.date}
                    <span className="inline-flex items-center gap-1"><Clock size={10} /> {i.read}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-tight mb-2">
                    {i.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {i.excerpt}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1 text-teal text-xs">
                    Read <ArrowUpRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
